import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import DownloadIcon from '@mui/icons-material/Download';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MoveDownOutlinedIcon from '@mui/icons-material/MoveDownOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  downloadMixConsolidationReport,
  getApiErrorMessage,
  getMixConsolidationConfig,
  getMixConsolidationReport,
  updateMixConsolidationConfig,
} from '../../../../api/mix-deliveriesApi';

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',

    year: 'numeric',
    month: '2-digit',
    day: '2-digit',

    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatQuantity = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '0';
  }

  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(numericValue);
};

const SummaryCard = ({ title, value, description, icon }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
    }}
  >
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',

            width: 44,
            height: 44,

            borderRadius: 2,
            bgcolor: 'action.hover',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>

          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              mt: 0.25,
            }}
          >
            {value}
          </Typography>

          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export default function MixConsolidationReport() {
  const [report, setReport] = useState(null);

  const [config, setConfig] = useState(null);

  const [toleranceInput, setToleranceInput] = useState('10');

  const [loading, setLoading] = useState(true);

  const [savingConfig, setSavingConfig] = useState(false);

  const [downloading, setDownloading] = useState(false);

  const [error, setError] = useState('');

  const [actionError, setActionError] = useState('');

  const [successMessage, setSuccessMessage] = useState('');

  const [search, setSearch] = useState('');

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(50);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    setActionError('');
    setSuccessMessage('');

    try {
      const [reportResult, configResult] = await Promise.all([
        getMixConsolidationReport(),
        getMixConsolidationConfig(),
      ]);

      setReport(reportResult);
      setConfig(configResult);

      setToleranceInput(String(configResult?.tolerancePercent ?? 10));
    } catch (requestError) {
      setReport(null);
      setConfig(null);

      setError(getApiErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const moves = useMemo(
    () => (Array.isArray(report?.moves) ? report.moves : []),
    [report],
  );

  const filteredMoves = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return moves;
    }

    return moves.filter((move) => {
      const searchableValues = [
        move.materialNumber,
        move.description,
        move.batch,
        move.stockType,
        move.unit,
        move.sourceStorageBin,
        move.targetStorageBin,
      ];

      return searchableValues.some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(normalizedSearch),
      );
    });
  }, [moves, search]);

  const visibleMoves = useMemo(() => {
    const start = page * rowsPerPage;

    return filteredMoves.slice(start, start + rowsPerPage);
  }, [filteredMoves, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [search, report]);

  const parsedTolerance = Number(toleranceInput);

  const toleranceValid =
    Number.isFinite(parsedTolerance) &&
    parsedTolerance >= 0 &&
    parsedTolerance <= 30;

  const savedTolerance = Number(config?.tolerancePercent ?? 10);

  const toleranceChanged = toleranceValid && parsedTolerance !== savedTolerance;

  const handleSaveConfig = async () => {
    if (!toleranceValid) {
      setActionError('Tolerancja musi mieścić się w zakresie od 0% do 30%.');

      return;
    }

    setSavingConfig(true);
    setActionError('');
    setSuccessMessage('');

    try {
      const updateResult = await updateMixConsolidationConfig(parsedTolerance);

      const updatedConfig = updateResult?.config ?? updateResult;

      setConfig(updatedConfig);

      setToleranceInput(
        String(updatedConfig?.tolerancePercent ?? parsedTolerance),
      );

      /*
       * Raport jest generowany na żądanie,
       * dlatego po zmianie konfiguracji
       * pobieramy go ponownie.
       */
      const updatedReport = await getMixConsolidationReport();

      setReport(updatedReport);

      setSuccessMessage('Konfiguracja została zapisana, a raport przeliczony.');
    } catch (requestError) {
      setActionError(getApiErrorMessage(requestError));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setActionError('');
    setSuccessMessage('');

    try {
      await downloadMixConsolidationReport();
    } catch (requestError) {
      setActionError(getApiErrorMessage(requestError));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 4,
        }}
      >
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          minHeight={260}
        >
          <CircularProgress />

          <Typography color="text.secondary">
            Generowanie raportu konsolidacji...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">{error}</Alert>

        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            Spróbuj ponownie
          </Button>
        </Box>
      </Stack>
    );
  }

  if (!report) {
    return (
      <Alert severity="info">
        Nie znaleziono danych do wygenerowania raportu konsolidacji.
      </Alert>
    );
  }

  const kegLimits = config?.kegLimits ?? report?.config?.kegLimits ?? {};

  return (
    <Stack spacing={3}>
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 2,
            md: 3,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{
            xs: 'stretch',
            md: 'flex-start',
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Konsolidacja strefy 0D20
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                maxWidth: 800,
              }}
            >
              Raport wskazuje ruchy umożliwiające zmniejszenie liczby zajętych
              lokalizacji. Konsolidowane są wyłącznie zapasy zgodne pod względem
              materiału, partii, rodzaju zapasu i jednostki miary.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={savingConfig || downloading}
            >
              Przelicz
            </Button>

            <Button
              variant="contained"
              startIcon={
                downloading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={handleDownload}
              disabled={downloading || savingConfig}
            >
              {downloading ? 'Generowanie...' : 'Pobierz Excel'}
            </Button>
          </Stack>
        </Stack>

        <Divider
          sx={{
            my: 2.5,
          }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Magazyn
            </Typography>

            <Typography variant="body2" fontWeight={600}>
              {report.warehouse || '—'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Snapshot zapasu
            </Typography>

            <Typography variant="body2" fontWeight={600}>
              {report.stockSnapshot?.fileName || '—'}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {formatDateTime(report.stockSnapshot?.createdAt)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Wiersze snapshotu
            </Typography>

            <Typography variant="body2" fontWeight={600}>
              {report.stockSnapshot?.rowCount ?? 0}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Wygenerowano
            </Typography>

            <Typography variant="body2" fontWeight={600}>
              {formatDateTime(report.generatedAt)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 2,
            md: 3,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{
            xs: 'stretch',
            md: 'flex-start',
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <SettingsOutlinedIcon />

              <Typography variant="h6" fontWeight={700}>
                Konfiguracja pojemności
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                maxWidth: 720,
              }}
            >
              Tolerancja zwiększa maksymalną pojemność binu dla produktów
              standardowych. Nie jest stosowana dla produktów KEG.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1}
            alignItems={{
              xs: 'stretch',
              sm: 'flex-start',
            }}
          >
            <TextField
              size="small"
              type="number"
              label="Tolerancja"
              value={toleranceInput}
              disabled={savingConfig}
              error={!toleranceValid}
              helperText={
                toleranceValid ? 'Zakres 0–30%' : 'Podaj wartość od 0 do 30'
              }
              inputProps={{
                min: 0,
                max: 30,
                step: 0.1,
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              onChange={(event) => {
                setToleranceInput(event.target.value);

                setActionError('');
                setSuccessMessage('');
              }}
              onKeyDown={(event) => {
                if (
                  event.key === 'Enter' &&
                  toleranceChanged &&
                  !savingConfig
                ) {
                  handleSaveConfig();
                }
              }}
              sx={{
                width: {
                  xs: '100%',
                  sm: 180,
                },
              }}
            />

            <Button
              variant="contained"
              startIcon={
                savingConfig ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
              onClick={handleSaveConfig}
              disabled={savingConfig || !toleranceValid || !toleranceChanged}
              sx={{
                minHeight: 40,
              }}
            >
              {savingConfig ? 'Zapisywanie...' : 'Zapisz i przelicz'}
            </Button>
          </Stack>
        </Stack>

        <Divider
          sx={{
            my: 2,
          }}
        />

        <Typography variant="subtitle2" gutterBottom>
          Sztywne limity dla KEG
        </Typography>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip
            variant="outlined"
            label={`KEG 20L: ${kegLimits.KEG20L ?? 24} szt.`}
          />

          <Chip
            variant="outlined"
            label={`KEG 30L: ${kegLimits.KEG30L ?? 18} szt.`}
          />

          <Chip
            variant="outlined"
            label={`KEG 50L: ${kegLimits.KEG50L ?? 12} szt.`}
          />
        </Stack>
      </Paper>

      {actionError ? (
        <Alert severity="error" onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      ) : null}

      {successMessage ? (
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            xl: 'repeat(5, 1fr)',
          },
          gap: 2,
        }}
      >
        <SummaryCard
          title="Grupy do konsolidacji"
          value={report.summary?.consolidationGroups ?? 0}
          description={`Przeanalizowane grupy: ${
            report.summary?.analyzedGroups ?? 0
          }`}
          icon={<Inventory2OutlinedIcon />}
        />

        <SummaryCard
          title="Ruchy magazynowe"
          value={report.summary?.moves ?? 0}
          description="Liczba proponowanych przesunięć"
          icon={<MoveDownOutlinedIcon />}
        />

        <SummaryCard
          title="Biny obecnie"
          value={report.summary?.currentBinsInCandidates ?? 0}
          description="W grupach możliwych do konsolidacji"
          icon={<WarehouseOutlinedIcon />}
        />

        <SummaryCard
          title="Biny po konsolidacji"
          value={report.summary?.binsAfterConsolidation ?? 0}
          description="Docelowa liczba lokalizacji"
          icon={<WarehouseOutlinedIcon />}
        />

        <SummaryCard
          title="Zwolnione biny"
          value={report.summary?.binsFreed ?? 0}
          description={`Pominięte grupy: ${report.summary?.skippedGroups ?? 0}`}
          icon={<MoveDownOutlinedIcon />}
        />
      </Box>

      <Paper variant="outlined">
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{
            xs: 'stretch',
            md: 'center',
          }}
          sx={{
            px: 2,
            py: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Proponowane ruchy magazynowe
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Każdy wiersz przedstawia ilość do przeniesienia pomiędzy
              konkretnymi binami.
            </Typography>
          </Box>

          <TextField
            size="small"
            label="Szukaj"
            placeholder="Materiał, partia lub bin"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              minWidth: {
                xs: '100%',
                md: 280,
              },
            }}
          />
        </Stack>

        <Divider />

        {moves.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography fontWeight={600}>
              Brak ruchów konsolidacyjnych.
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
              }}
            >
              Aktualny zapas nie pozwala zmniejszyć liczby zajętych binów przy
              obowiązujących limitach.
            </Typography>
          </Box>
        ) : filteredMoves.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography>
              Brak pozycji spełniających kryterium wyszukiwania.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              sx={{
                maxHeight: 620,
              }}
            >
              <Table
                stickyHeader
                size="small"
                aria-label="Ruchy konsolidacyjne strefy 0D20"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Lp.</TableCell>

                    <TableCell>Materiał</TableCell>

                    <TableCell>Nazwa</TableCell>

                    <TableCell>Partia</TableCell>

                    <TableCell>Rodzaj zapasu</TableCell>

                    <TableCell align="right">Ilość źródłowa</TableCell>

                    <TableCell>JM</TableCell>

                    <TableCell>Bin źródłowy</TableCell>

                    <TableCell>Bin docelowy</TableCell>

                    <TableCell align="right">Ilość po przesunięciu</TableCell>

                    <TableCell align="right">Maks. ilość</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleMoves.map((move, index) => (
                    <TableRow
                      hover
                      key={[
                        move.groupKey,
                        move.sequence,
                        move.sourceStorageBin,
                        move.targetStorageBin,
                      ].join('-')}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                      <TableCell
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {move.materialNumber}
                      </TableCell>

                      <TableCell
                        sx={{
                          minWidth: 240,
                        }}
                      >
                        {move.description || '—'}
                      </TableCell>

                      <TableCell>{move.batch || '—'}</TableCell>

                      <TableCell>{move.stockType || '—'}</TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatQuantity(move.quantity)}
                      </TableCell>

                      <TableCell>{move.unit || '—'}</TableCell>

                      <TableCell
                        sx={{
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {move.sourceStorageBin || '—'}
                      </TableCell>

                      <TableCell
                        sx={{
                          whiteSpace: 'nowrap',
                          fontWeight: 600,
                        }}
                      >
                        {move.targetStorageBin || '—'}
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatQuantity(move.targetQuantityAfterMove)}
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatQuantity(move.maxQuantityPerBin)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredMoves.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Wierszy na stronie:"
              onPageChange={(_event, newPage) => {
                setPage(newPage);
              }}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));

                setPage(0);
              }}
            />
          </>
        )}
      </Paper>
    </Stack>
  );
}
